/** Zona del producto: todo el tenis de Torneos123 se juega y se cobra en CDMX. */
const PRODUCT_TIME_ZONE = "America/Mexico_City";

/* Marca con zona explícita ("Z" o "±hh:mm") y hora: viene del servidor en UTC
 * (created_at/paid_at de órdenes, etc.) y se muestra en hora CDMX. Las marcas
 * NAIVE (sin zona) se dejan tal cual: son hora CDMX a propósito (sorteos,
 * scheduled_at) o fechas puras. */
const AWARE_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/;

let cdmxFormatter: Intl.DateTimeFormat | null = null;

function cdmxParts(value: string): { date: string; time: string } | null {
  if (!AWARE_DATETIME_RE.test(value)) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  try {
    cdmxFormatter ??= new Intl.DateTimeFormat("es-MX", {
      timeZone: PRODUCT_TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    const parts: Record<string, string> = {};
    for (const part of cdmxFormatter.formatToParts(parsed)) parts[part.type] = part.value;
    if (!parts.year || !parts.month || !parts.day || !parts.hour || !parts.minute) return null;
    return {
      date: `${parts.day.padStart(2, "0")}/${parts.month.padStart(2, "0")}/${parts.year}`,
      time: `${parts.hour.padStart(2, "0")}:${parts.minute.padStart(2, "0")}`,
    };
  } catch {
    return null;
  }
}

export function formatDate(value: string | null | undefined, fallback = "—"): string {
  if (!value) return fallback;

  const cdmx = cdmxParts(value);
  if (cdmx) return cdmx.date;

  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${day}/${month}/${year}`;
  }

  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    const day = String(parsed.getDate()).padStart(2, "0");
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const year = parsed.getFullYear();
    return `${day}/${month}/${year}`;
  }

  return value;
}

export function formatDateTime(value: string | null | undefined, fallback = "—"): string {
  if (!value) return fallback;

  const cdmx = cdmxParts(value);
  if (cdmx) return `${cdmx.date} ${cdmx.time}`;

  const date = formatDate(value, fallback);
  const timeMatch = /T(\d{2}:\d{2})/.exec(value) ?? /\s(\d{2}:\d{2})/.exec(value);
  return timeMatch ? `${date} ${timeMatch[1]}` : date;
}

let cdmxDayFormatter: Intl.DateTimeFormat | null = null;

/* Día de calendario en CDMX como número (días desde 1970), para contar
 * «hoy», «ayer» o «hace N días» con el día de México y no el de UTC. */
function cdmxDayNumber(ms: number): number | null {
  try {
    cdmxDayFormatter ??= new Intl.DateTimeFormat("en-US", {
      timeZone: PRODUCT_TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const parts: Record<string, string> = {};
    for (const part of cdmxDayFormatter.formatToParts(new Date(ms))) parts[part.type] = part.value;
    const year = Number(parts.year);
    const month = Number(parts.month);
    const day = Number(parts.day);
    if (!year || !month || !day) return null;
    return Math.round(Date.UTC(year, month - 1, day) / 86_400_000);
  } catch {
    return null;
  }
}

/** Último ingreso de una cuenta (lista de admins del superadmin):
 * "Último ingreso: 29/09/2026 09:40 h (hoy)", "(ayer)", "(hace 5 días)";
 * sin marca, "Nunca ha entrado". */
export function describeLastAccess(value: string | null | undefined, nowMs: number = Date.now()): string {
  if (!value) return "Nunca ha entrado";
  const when = `Último ingreso: ${formatDateTime(value)} h`;
  if (!AWARE_DATETIME_RE.test(value)) return when;
  const then = cdmxDayNumber(Date.parse(value));
  const today = cdmxDayNumber(nowMs);
  if (then === null || today === null) return when;
  const days = today - then;
  if (days < 0) return when;
  if (days === 0) return `${when} (hoy)`;
  if (days === 1) return `${when} (ayer)`;
  return `${when} (hace ${days} días)`;
}

export function formatDateWithTime(
  date: string | null | undefined,
  time?: string | null,
  fallback = "Por confirmar",
): string {
  if (!date) return fallback;
  return `${formatDate(date, fallback)}${time ? ` ${time.slice(0, 5)}` : ""}`;
}

/* Fecha del calendario en palabras (Eduardo 2026-10-01: «me pierdo con la
 * fecha»). Arreglos propios en lugar de Intl: el texto sale igual en todos
 * los navegadores y sin la coma de "jueves, 1 de octubre". Las fechas son
 * días puros (YYYY-MM-DD), sin hora ni zona. */
const WEEKDAYS_ES = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const WEEKDAYS_SHORT_ES = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const MONTHS_ES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];
const MONTHS_SHORT_ES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

function isoDayParts(value: string | null | undefined): { year: number; month: number; day: number; weekday: number } | null {
  const match = value ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim()) : null;
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  // 2026-02-31 no existe: Date lo "corre" a marzo y ya no coincide.
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return { year, month, day, weekday: date.getDay() };
}

/** "2026-10-01" → "Jueves 1 de octubre de 2026"; vacío si no es una fecha. */
export function formatLongDateEs(value: string | null | undefined): string {
  const parts = isoDayParts(value);
  if (!parts) return "";
  return `${WEEKDAYS_ES[parts.weekday]} ${parts.day} de ${MONTHS_ES[parts.month - 1]} de ${parts.year}`;
}

/** "2026-10-01" → "Jueves"; vacío si no es una fecha. */
export function weekdayEs(value: string | null | undefined): string {
  const parts = isoDayParts(value);
  return parts ? WEEKDAYS_ES[parts.weekday] : "";
}

/** "2026-10-01" → { weekday: "Jue", day: "1 oct" } para espacios angostos. */
export function compactDateEs(value: string | null | undefined): { weekday: string; day: string } | null {
  const parts = isoDayParts(value);
  if (!parts) return null;
  return { weekday: WEEKDAYS_SHORT_ES[parts.weekday], day: `${parts.day} ${MONTHS_SHORT_ES[parts.month - 1]}` };
}

/** "2026-10-03" → "sábado 3 de octubre" (dentro de una frase, sin año);
 * vacío si no es una fecha. */
export function dayMonthEs(value: string | null | undefined): string {
  const parts = isoDayParts(value);
  if (!parts) return "";
  return `${WEEKDAYS_ES[parts.weekday].toLowerCase()} ${parts.day} de ${MONTHS_ES[parts.month - 1]}`;
}

/** "2026-10-03" → "sáb 3 oct" (chips y avisos cortos); vacío si no es una fecha. */
export function shortDayMonthEs(value: string | null | undefined): string {
  const compact = compactDateEs(value);
  return compact ? `${compact.weekday.toLowerCase()} ${compact.day}` : "";
}

/** Fecha y hora (CDMX) dentro de una frase: «5 de octubre · 9:07 h» o, sin
 * hora, «10 de septiembre de 2026» (`withYear`). Vacío si no es fecha. Solo
 * para fechas con hora (published_at, performed_at…). */
export function cdmxDayMonthEs(
  value: string | null | undefined,
  { withTime = false, withYear = false }: { withTime?: boolean; withYear?: boolean } = {},
): string {
  const text = formatDateTime(value, "");
  const found = /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?/.exec(text);
  if (!found) return "";
  const [, dd, mm, yyyy, hh, min] = found;
  const month = MONTHS_ES[Number(mm) - 1];
  if (!month) return "";
  const date = `${Number(dd)} de ${month}${withYear ? ` de ${yyyy}` : ""}`;
  return withTime && hh ? `${date} · ${Number(hh)}:${min} h` : date;
}
